import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User } from '@/types';
import { apiService } from '@/services/api';
import { storage } from '@/utils';
import i18n from '@/i18n';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  register: (userData: any) => Promise<void>;
  updateUser: (userData: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Перевіряємо збереженого користувача при завантаженні
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const token = storage.get<string | null>('auth_token', null);
        if (token) {
          // Отримуємо збереженого користувача з localStorage
          const savedUser = storage.get<User | null>('user', null);
          if (savedUser) {
            setUser(savedUser);
          }
        }
        
        // При перезавантаженні НЕ перезаписуємо localStorage з об'єкта користувача!
        // Використовуємо мову з localStorage (яка могла бути змінена через ленг світчер)
        const savedLanguage = localStorage.getItem('language');
        if (savedLanguage && i18n.language !== savedLanguage) {
          i18n.changeLanguage(savedLanguage);
        }
      } catch (error) {
        console.error('Помилка ініціалізації автентифікації:', error);
        storage.remove('auth_token');
        storage.remove('user');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Синхронізуємо мову i18n з localStorage (не з об'єкта користувача!)
  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    if (savedLanguage && i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
  }, []);

  const login = async (email: string, password: string) => {
    setIsLoading(true);
    try {
      const { user: userData, token } = await apiService.login(email, password);
      
      console.log('Login - userData:', userData);
      
      setUser(userData);
      storage.set('auth_token', token);
      storage.set('user', userData);
      
      // При авторизації завжди записуємо мову з API в localStorage
      if (userData.language) {
        localStorage.setItem('language', userData.language);
        i18n.changeLanguage(userData.language);
      }
    } catch (error) {
      console.error('Помилка входу:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData: any) => {
    setIsLoading(true);
    try {
      const { user: newUser, token } = await apiService.register(userData);
      
      setUser(newUser);
      storage.set('auth_token', token);
      storage.set('user', newUser);
    } catch (error) {
      console.error('Signin error:', error);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    setIsLoading(true);
    try {
      // Logout without API call - just clear local data
      setUser(null);
      storage.remove('auth_token');
      storage.remove('user');
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    register,
    updateUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
